import React, { useEffect, useRef, useState } from 'react';
import { ManageInWorkspace, workspaceGitLine } from '../components/settings/ManageInWorkspace';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { toast } from 'react-toastify';
import { aaiApi } from '../services/aaiApi';
import type { ConnectorScope, OrbitToolBoard, OrbitToolRow } from '../types/aai';
import { useAsync } from './aai/useAsync';
import { Action, Banner, Chip, EmptyState, ErrorNote, Loading, Panel, PanelHead } from '../components/aa';
import { ToolControlModal } from './aai/insights/MyToolsPage';

/*
 * Every tool you may reach, beside the ones you have connected.
 *
 * This screen listed connections, so a tool you had not connected yet did not
 * exist on it and the only way to find one was to already know its name. The
 * list below is the catalogue, scoped by the server the same way the company's
 * tools board is: the Owner sees all of them because assigning one means seeing
 * the ones nobody has been given, and everybody else sees what their own
 * department has been given.
 *
 * Connecting happens here too. The sign-in is the tool's own, in a popup, and
 * the scope follows who is doing it: somebody who manages the company's
 * connections makes one the company uses, and everybody else makes their own.
 * Nobody is offered a tool their department was not given, because the list
 * itself is already scoped, so "can I connect this" never has to be asked
 * twice.
 */

/* Why this banner is up, in the person's own terms. */
const bannerDesc = (tool: OrbitToolRow | undefined, live: boolean): string => {
  if (!tool) return 'Nothing in the catalogue matches that name, so there is nothing to connect here yet.';
  if (live) return 'Nothing to do. Head back to the work that sent you.';
  if (!tool.connector?.connect_here) return tool.connector?.setup_hint || 'This one is connected somewhere else.';
  return 'The work that sent you here needs this connected. The sign-in happens on the tool\'s own site.';
};

const norm = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '');

const initials = (label: string): string => label.replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase();

export const AvailableTools: React.FC = () => {
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState('');
  // Whose permissions are open. A connected tool is the only one with anything
  // to govern, so it is the only one that opens this.
  const [governing, setGoverning] = useState('');
  const board = useAsync<OrbitToolBoard>(() => aaiApi.getOrbitTools(), 'Could not load the tool catalogue.');
  const pollRef = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearInterval(pollRef.current), []);

  /* Somebody was sent here to connect one particular tool.
     The build workspace links to /my-tools?connect=<tool> when a job needs a
     connection it cannot make itself: connecting runs on the person's own
     identity, which lives on this side. Until this read the parameter, that
     handoff landed on the catalogue with no idea why it had been opened.
     It is answered with a banner and a button, not by opening the sign-in on
     load: an OAuth popup with no click behind it is what popup blockers are
     for. */
  const [params, setParams] = useSearchParams();
  const asked = (params.get('connect') || '').trim().toLowerCase();
  const askedRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (asked && askedRef.current) askedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [asked, board.data]);
  const clearAsked = () => {
    const next = new URLSearchParams(params);
    next.delete('connect');
    setParams(next, { replace: true });
  };

  /* The popup closing is not the same as the connection landing: the provider
     redirects back and the record is written a moment later. So this asks
     rather than assumes, which is what stops a row saying "connected" before
     it is. */
  const settled = async (slug: string, scope: ConnectorScope): Promise<boolean> => {
    for (let i = 0; i < 8; i += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await aaiApi.getConnectorStatus(slug, scope);
        if (res.data.connected) return true;
      } catch {
        // Still finishing. Keep asking.
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => { window.setTimeout(resolve, 1300); });
    }
    return false;
  };

  const connect = async (tool: OrbitToolRow, scope: ConnectorScope) => {
    if (!tool.connector) return;
    const slug = tool.connector.slug;
    setBusy(tool.key);
    try {
      const res = await aaiApi.initiateConnector(slug, scope);
      if (res.data.alreadyConnected) {
        toast.info(`${tool.label} was already authorized.`);
        board.reload();
        setBusy('');
        return;
      }
      if (!res.data.redirectUrl) {
        toast.error(`Could not start the ${tool.label} connection.`);
        setBusy('');
        return;
      }
      const popup = window.open(res.data.redirectUrl, `connect-${slug}`, 'width=600,height=720,menubar=no,toolbar=no');
      if (!popup || popup.closed) {
        // Popup blocked: the sign-in still has to happen, so go to it in place
        // rather than failing with nothing the person can do about it.
        window.location.href = res.data.redirectUrl;
        return;
      }
      pollRef.current = window.setInterval(async () => {
        if (!popup.closed) return;
        window.clearInterval(pollRef.current);
        const ok = await settled(slug, scope);
        board.reload();
        setBusy('');
        if (ok) {
          const whose = scope === 'organization' ? 'The company uses this one.' : 'It stays yours alone.';
          toast.success(`${tool.label} connected. ${whose} Read-only until an action is turned on.`);
          if (norm(asked) === norm(tool.key)) clearAsked();
          return;
        }
        toast.error(`${tool.label} did not finish connecting. If you closed the window early, try again.`);
      }, 900);
    } catch (err) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        || `Could not connect ${tool.label}.`);
      setBusy('');
    }
  };

  if (board.loading) return <Loading label="Loading the tools you can reach" />;
  if (board.error || !board.data) return <ErrorNote message={board.error || 'Something went wrong.'} />;

  // Who the sign-in belongs to. Somebody who manages the company's connections
  // makes one the company uses; everybody else makes their own, which is the
  // same rule the company's tools screen applies.
  const canManage = Boolean(board.data.can_manage);
  let connectFor = 'Your own sign-in';
  if (canManage) connectFor = 'For the whole company';
  let headDesc = 'Connect your own account to use an app. The sign-in happens on the app\'s own site, and what an assistant may do in it is decided per action.';
  if (canManage) headDesc = 'Connecting here shares the app with the whole company. To connect only your own account, use Settings, Connectors.';

  const needle = query.trim().toLowerCase();
  const shown = board.data.tools.filter(
    (t) => !needle || `${t.label} ${t.category_label}`.toLowerCase().includes(needle),
  );

  const wanted = asked
    ? board.data.tools.find((t) => norm(t.key) === norm(asked) || norm(t.label) === norm(asked))
    : undefined;
  const wantedLive = Boolean(
    wanted?.connector
    && (wanted.connector.personal_status === 'connected' || wanted.connector.organization_status === 'connected'),
  );

  return (
    <Panel>
      <PanelHead
        kicker="Catalogue"
        title="Apps you can connect"
        desc={headDesc}
      />
      {asked && (
        <div ref={askedRef} className="px-5 pb-4 md:px-6">
          <Banner
            tone={wantedLive ? 'quiet' : 'surface'}
            kicker="Sent here to connect"
            title={wanted ? `${wanted.label}${wantedLive ? ' is already connected' : ''}` : `We have no connector for "${asked}"`}
            desc={bannerDesc(wanted, wantedLive)}
            action={(
              <div className="flex items-center gap-2">
                {wanted && !wantedLive && wanted.connector?.connect_here && (
                  <Action
                    size="sm"
                    disabled={busy === wanted.key || !wanted.connector.enabled}
                    onClick={() => connect(wanted, canManage ? 'organization' : 'personal')}
                  >
                    {busy === wanted.key ? 'Connecting' : `Connect ${wanted.label}`}
                  </Action>
                )}
                <Action size="sm" variant="quiet" onClick={clearAsked}>Dismiss</Action>
              </div>
            )}
          />
        </div>
      )}

      <div className="px-5 pb-4 md:px-6">
        <div className="flex h-9 items-center gap-2 rounded-act border border-line bg-white px-3">
          <Search size={15} className="shrink-0 text-ink-soft" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the catalogue"
            aria-label="Search the tool catalogue"
            className="w-full border-0 bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-soft"
          />
        </div>
      </div>

      {shown.length === 0 && (
        <EmptyState
          title={needle ? `Nothing matches "${query}"` : 'No tools available to you yet'}
          reason={needle
            ? 'No tool by that name can be connected from here.'
            : 'A tool appears here once it has been given to your department.'}
        />
      )}

      {shown.length > 0 && (
        <div className="grid grid-cols-1 gap-3 px-5 pb-5 sm:grid-cols-2 lg:grid-cols-3 md:px-6">
          {shown.map((t) => {
            const connected = Boolean(
              t.connector && (t.connector.personal_status === 'connected' || t.connector.organization_status === 'connected'),
            );
            return (
              <div key={t.key} className="flex h-full flex-col rounded-card border border-line bg-white p-4">
                <div className="flex items-start gap-3">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-tile bg-canvas-inset text-[11px] font-bold text-ink-muted">
                    {initials(t.label)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13.5px] font-semibold text-ink">{t.label}</div>
                    <div className="mt-0.5 truncate text-[12px] text-ink-dim">{t.category_label}</div>
                  </div>
                </div>
                {(connected || t.on_stack || (t.connector && !t.connector.enabled)) && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {connected && <Chip tone="ok">Connected</Chip>}
                    {t.on_stack && !connected && <Chip>On your stack</Chip>}
                    {t.connector && !t.connector.enabled && <Chip tone="warn">Switched off</Chip>}
                  </div>
                )}
                {t.used_in_skills.length > 0 && (
                  <div className="mt-2 text-[11.5px] leading-[17px] text-ink-soft">
                    Used by {t.used_in_skills.map((s) => s.name).join(', ')}
                  </div>
                )}
                <div className="mt-auto pt-4">
                  {connected && (
                    <Action size="sm" variant="quiet" className="w-full whitespace-nowrap" onClick={() => setGoverning(t.key)}>
                      What it may do
                    </Action>
                  )}
                  {!connected && t.connector?.connect_here && (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[11.5px] text-ink-soft">{connectFor}</span>
                      <Action
                        size="sm"
                        variant="quiet"
                        className="shrink-0 whitespace-nowrap"
                        disabled={busy === t.key || !t.connector.enabled}
                        reason={t.connector.enabled ? 'Connecting' : 'Your company has switched this one off'}
                        onClick={() => connect(t, canManage ? 'organization' : 'personal')}
                      >
                        {busy === t.key ? 'Connecting' : 'Connect'}
                      </Action>
                    </div>
                  )}
                  {!connected && t.connector && !t.connector.connect_here && (
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-[11.5px] leading-[17px] text-ink-soft">{workspaceGitLine(t.connector)}</div>
                      <ManageInWorkspace row={t.connector} size="xs" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {governing && <ToolControlModal tool={governing} onClose={() => { setGoverning(''); board.reload(); }} />}
    </Panel>
  );
};

export default AvailableTools;
